import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  startCreation, listCreations, getCreationDetail,
  approveSocialDraft, sendEmailBroadcast, archiveCreation,
} from "@/lib/pipeline.functions";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Sparkles, Archive, Send, Check, X, ExternalLink } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/pipeline")({
  head: () => ({ meta: [{ title: "Creation Pipeline · KAfrequency Admin" }] }),
  component: PipelinePage,
});

type Listing = Awaited<ReturnType<typeof listCreations>>;
type Detail = Awaited<ReturnType<typeof getCreationDetail>>;

function PipelinePage() {
  const start = useServerFn(startCreation);
  const list = useServerFn(listCreations);
  const detail = useServerFn(getCreationDetail);
  const approve = useServerFn(approveSocialDraft);
  const sendBroadcast = useServerFn(sendEmailBroadcast);
  const archive = useServerFn(archiveCreation);

  const [items, setItems] = useState<Listing>([]);
  const [selected, setSelected] = useState<Detail | null>(null);
  const [form, setForm] = useState({ title: "", body: "", hint: "", publish_at: "" });
  const [busy, setBusy] = useState(false);

  const refresh = () => list().then(setItems).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const openDetail = async (id: string) => {
    try { setSelected(await detail({ data: { id } })); }
    catch (e) { toast.error((e as Error).message); }
  };

  const onCreate = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    setBusy(true);
    try {
      const result = await start({
        data: {
          title: form.title.trim(),
          body: form.body.trim() || undefined,
          seed_hint: form.hint.trim() || undefined,
          publish_at: form.publish_at ? new Date(form.publish_at).toISOString() : undefined,
        },
      });
      toast.success(`Created "${result.slug}" as ${result.category}`);
      setForm({ title: "", body: "", hint: "", publish_at: "" });
      await refresh();
      await openDetail(result.creation_id);
    } catch (e) {
      toast.error((e as Error).message);
    } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 md:p-6">
      <div>
        <h1 className="font-serif text-3xl text-gold-luminous">Creation Pipeline</h1>
        <p className="mt-1 text-sm text-foreground/60">Create once. AI categorizes, generates artwork, schedules distribution, drafts social, emails subscribers, and publishes the SEO page.</p>
      </div>

      <Card className="p-5">
        <div className="text-xs uppercase tracking-widest text-gold/70">New Creation</div>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <Input
            placeholder="Title (required)"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
          <Input
            type="datetime-local"
            placeholder="Publish at (optional)"
            value={form.publish_at}
            onChange={(e) => setForm({ ...form, publish_at: e.target.value })}
          />
        </div>
        <Textarea
          className="mt-3"
          placeholder="Body / teaching / description (optional — AI can infer)"
          rows={5}
          value={form.body}
          onChange={(e) => setForm({ ...form, body: e.target.value })}
        />
        <Input
          className="mt-3"
          placeholder="Hint to AI (e.g. 'this is a song release', 'daily activation for gate 12')"
          value={form.hint}
          onChange={(e) => setForm({ ...form, hint: e.target.value })}
        />
        <div className="mt-4 flex justify-end">
          <Button onClick={onCreate} disabled={busy}>
            <Sparkles size={16} className="mr-2" /> {busy ? "Running pipeline…" : "Create once → distribute everywhere"}
          </Button>
        </div>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        <Card className="p-4">
          <div className="mb-3 text-xs uppercase tracking-widest text-gold/70">Creations</div>
          <div className="space-y-2 max-h-[600px] overflow-auto">
            {items.length === 0 && <div className="text-sm text-foreground/50">Nothing yet — create your first above.</div>}
            {items.map((c) => (
              <button
                key={c.id}
                onClick={() => openDetail(c.id)}
                className={`flex w-full items-center gap-3 rounded border p-3 text-left hover:border-gold/50 ${selected?.creation?.id === c.id ? "border-gold/60" : "border-white/10"}`}
              >
                {c.artwork_url ? (
                  <img src={c.artwork_url} alt="" className="h-12 w-12 rounded object-cover" />
                ) : (
                  <div className="h-12 w-12 rounded bg-black/40" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm text-foreground">{c.title}</div>
                  <div className="mt-0.5 flex items-center gap-2 text-[10px] text-foreground/50">
                    <Badge variant="outline" className="text-[9px]">{c.ai_category ?? "?"}</Badge>
                    <Badge variant="outline" className="text-[9px]">{c.status}</Badge>
                    <span>{new Date(c.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </Card>

        <Card className="p-4">
          {!selected || !selected.creation ? (
            <div className="p-4 text-sm text-foreground/50">Select a creation to see distribution status, social drafts, and email broadcast.</div>
          ) : (
            <DetailPanel
              data={selected as Detail & { creation: NonNullable<Detail["creation"]> }}
              onApprove={async (id, action, external_url) => {
                await approve({ data: { id, action, external_url } });
                if (selected?.creation) await openDetail(selected.creation.id);
              }}
              onSend={async (id) => {
                try {
                  const r = await sendBroadcast({ data: { id } });
                  toast.success(`Sent to ${(r as any).sent ?? 0} / ${(r as any).recipients ?? 0}`);
                  if (selected?.creation) await openDetail(selected.creation.id);
                } catch (e) { toast.error((e as Error).message); }
              }}
              onArchive={async () => {
                if (!selected?.creation) return;
                await archive({ data: { id: selected.creation.id } });
                toast.success("Archived");
                setSelected(null);
                refresh();
              }}
            />
          )}
        </Card>
      </div>
    </div>
  );
}

function DetailPanel({ data, onApprove, onSend, onArchive }: {
  data: Detail & { creation: NonNullable<Detail["creation"]> };
  onApprove: (id: string, action: "approve" | "skip" | "posted", external_url?: string) => Promise<void>;
  onSend: (id: string) => Promise<void>;
  onArchive: () => Promise<void>;
}) {
  const c = data.creation;
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        {c.artwork_url && <img src={c.artwork_url} alt="" className="h-20 w-20 rounded object-cover" />}
        <div className="min-w-0 flex-1">
          <div className="truncate font-serif text-lg text-gold-luminous">{c.title}</div>
          <div className="mt-1 flex flex-wrap gap-1 text-[10px]">
            <Badge variant="outline">{c.ai_category ?? "?"}</Badge>
            <Badge variant="outline">{c.status}</Badge>
            {c.slug && (
              <a href={`/release/${c.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-gold underline">
                /release/{c.slug} <ExternalLink size={10} />
              </a>
            )}
          </div>
          {c.ai_summary && <p className="mt-2 text-xs text-foreground/70">{c.ai_summary}</p>}
        </div>
        <Button size="sm" variant="ghost" onClick={onArchive}><Archive size={14} /></Button>
      </div>

      <section>
        <div className="text-[10px] uppercase tracking-widest text-gold/60">Distribution</div>
        <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
          {data.distributions.map((d) => (
            <div key={d.id} className="rounded border border-white/10 p-2">
              <div className="flex items-center justify-between">
                <span className="capitalize">{d.channel}</span>
                <Badge variant="outline" className="text-[9px]">{d.status}</Badge>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <div className="text-[10px] uppercase tracking-widest text-gold/60">Social drafts ({data.socials.length})</div>
        <div className="mt-2 space-y-2">
          {data.socials.map((s) => (
            <div key={s.id} className="rounded border border-white/10 p-3 text-xs">
              <div className="flex items-center justify-between">
                <Badge variant="outline" className="text-[9px] capitalize">{s.platform}</Badge>
                <Badge variant="outline" className="text-[9px]">{s.status}</Badge>
              </div>
              <p className="mt-2 text-foreground/80">{s.caption}</p>
              {s.hashtags?.length > 0 && (
                <div className="mt-1 text-[10px] text-gold/60">{s.hashtags.map((h: string) => `#${h}`).join(" ")}</div>
              )}
              {s.status !== "posted" && s.status !== "skipped" && (
                <div className="mt-2 flex gap-2">
                  {s.status === "queued" && (
                    <Button size="sm" variant="outline" onClick={() => onApprove(s.id, "approve")}>
                      <Check size={12} className="mr-1" /> Approve
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => {
                    const url = prompt("Enter posted URL (optional)") || undefined;
                    onApprove(s.id, "posted", url);
                  }}>Mark posted</Button>
                  <Button size="sm" variant="ghost" onClick={() => onApprove(s.id, "skip")}>
                    <X size={12} />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      <section>
        <div className="text-[10px] uppercase tracking-widest text-gold/60">Email broadcast</div>
        {data.broadcasts.map((b) => (
          <div key={b.id} className="mt-2 rounded border border-white/10 p-3 text-xs">
            <div className="flex items-center justify-between">
              <div className="font-medium text-foreground">{b.subject}</div>
              <Badge variant="outline" className="text-[9px]">{b.status}</Badge>
            </div>
            <div className="mt-1 text-foreground/60">{b.preview_text}</div>
            {b.status === "sent" && (
              <div className="mt-1 text-[10px] text-foreground/50">
                {b.sent_count}/{b.recipient_count} sent · {b.suppressed_count} suppressed · {b.failed_count} failed
              </div>
            )}
            {(b.status === "draft" || b.status === "approved" || b.status === "failed") && (
              <Button size="sm" className="mt-2" onClick={() => {
                if (confirm(`Send to all active subscribers?`)) onSend(b.id);
              }}>
                <Send size={12} className="mr-1" /> Send now
              </Button>
            )}
          </div>
        ))}
      </section>
    </div>
  );
}
